import { useEffect, useState } from "react";
import { IKRA_LOGO, NAV_LINKS, INSTAGRAM_URL } from "@/data/content";
import { Menu, X, Instagram } from "lucide-react";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="ikra-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--ikra-bg)]/80 backdrop-blur-xl border-b border-[var(--ikra-line)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
          onClick={close}
        >
          <span className="inline-flex h-11 w-11 items-center justify-center overflow-hidden">
            <img
              src={IKRA_LOGO}
              alt="Ikra"
              className="h-11 w-11 object-contain transition-transform duration-500 group-hover:rotate-[8deg]"
              style={{ mixBlendMode: "screen" }}
            />
          </span>
          <span className="font-display font-semibold text-lg text-[var(--ikra-ink)] tracking-tight leading-none">
            IKRA
            <span className="block text-[9px] font-body font-normal tracking-[0.22em] uppercase text-[var(--ikra-gold)] mt-1">
              Marketing &amp; Sales
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="ikra-link text-sm text-[var(--ikra-ink)]/75 hover:text-[var(--ikra-ink)] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram Ikra Białystok"
            data-testid="nav-instagram-link"
            className="inline-flex items-center gap-2 h-10 px-3 rounded-full border border-[var(--ikra-line)] text-[var(--ikra-ink)]/70 hover:text-[var(--ikra-gold)] hover:border-[var(--ikra-gold)] transition-colors"
          >
            <Instagram size={16} />
            <span className="hidden xl:inline text-xs tracking-wide">ikra_bialystok</span>
          </a>
          <a
            href="#aplikuj"
            data-testid="nav-cta-apply"
            className="hidden md:inline-flex btn-primary items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
          >
            Dołącz do zespołu
            <span aria-hidden>→</span>
          </a>
          <button
            data-testid="nav-mobile-toggle"
            type="button"
            aria-label="Menu"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-[var(--ikra-line)] text-[var(--ikra-ink)]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-[var(--ikra-line)] bg-[var(--ikra-bg)]"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={close}
                className="text-lg font-medium text-[var(--ikra-ink)]"
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#aplikuj"
              onClick={close}
              data-testid="nav-mobile-cta-apply"
              className="mt-2 inline-flex btn-primary items-center justify-center px-5 py-3 rounded-full text-sm font-medium"
            >
              Dołącz do zespołu →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
